import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  adminApi, defaultProctorSettings,
  type OtpMode, type Quiz, type QuizInput, type ShowResults, type Shortfall,
} from "@/lib/adminApi";
import { adminAuth } from "@/lib/adminAuth";
import { canManageQuestions } from "@/lib/roles";

// Local-datetime helpers: <input type="datetime-local"> works in the browser's local zone
// but the API expects ISO/UTC. Convert on the boundary.
const toLocalInput = (iso: string) => {
  const d = new Date(iso);
  const pad = (n: number) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
};
const fromLocalInput = (v: string) => new Date(v).toISOString();

export default function AdminQuizEditor() {
  const { quizId } = useParams();
  const isNew = !quizId || quizId === "new";
  const navigate = useNavigate();
  const qc = useQueryClient();

  const existing = useQuery({
    queryKey: ["quiz", quizId],
    queryFn: () => adminApi.getQuiz(quizId!),
    enabled: !isNew,
  });

  const [form, setForm] = useState<QuizInput>(() => blankForm());
  const [error, setError] = useState<string | null>(null);
  const [savedAt, setSavedAt] = useState<Date | null>(null);
  const [candidateCategoriesText, setCategoriesText] = useState(
    isNew ? "General, SC, ST, OBC" : "",
  );
  const [initialCategoryIds, setInitialCategoryIds] = useState<Set<string>>(new Set());

  // Auto-hide the "Saved" pill after 4s.
  useEffect(() => {
    if (!savedAt) return;
    const t = setTimeout(() => setSavedAt(null), 4000);
    return () => clearTimeout(t);
  }, [savedAt]);

  const categoriesQ = useQuery({
    queryKey: ["categories"],
    queryFn: () => adminApi.listCategories(),
    enabled: isNew, // only used for the pre-create picker
  });

  // Pull system defaults on the New Quiz form so the camera interval + enabled flag
  // come from the admin's global setting instead of hard-coded values.
  const systemSettings = useQuery({
    queryKey: ["system-settings"],
    queryFn: () => adminApi.getSettings(),
    enabled: isNew,
  });

  useEffect(() => {
    if (existing.data) {
      setForm(quizToInput(existing.data));
      setCategoriesText((existing.data.candidateCategories ?? []).join(", "));
    }
  }, [existing.data]);

  // Apply system defaults into the blank form once they load (new quiz only).
  useEffect(() => {
    if (!isNew || !systemSettings.data) return;
    setForm((f) => ({
      ...f,
      proctorSettings: f.proctorSettings ? {
        ...f.proctorSettings,
        camera: {
          ...f.proctorSettings.camera,
          enabled: systemSettings.data.cameraEnabledByDefault,
          intervalMinutes: systemSettings.data.cameraIntervalMinutes,
          intervalSeconds: systemSettings.data.cameraIntervalSeconds,
        },
      } : null,
    }));
  }, [isNew, systemSettings.data]);

  const save = useMutation({
    mutationFn: async () => {
      const body: QuizInput = {
        ...form,
        candidateCategories: candidateCategoriesText
          .split(",").map((s) => s.trim()).filter(Boolean),
      };
      if (isNew) {
        const q = await adminApi.createQuiz(body);
        // Attach any question categories the admin picked on the create form.
        await Promise.all(
          Array.from(initialCategoryIds).map((cid) => adminApi.attachCategory(q.id, cid)),
        );
        return q;
      }
      return adminApi.updateQuiz(quizId!, body);
    },
    onSuccess: (q) => {
      qc.invalidateQueries({ queryKey: ["quizzes"] });
      qc.invalidateQueries({ queryKey: ["quiz", q.id] });
      setError(null);
      setSavedAt(new Date());
      if (isNew) navigate(`/admin/quizzes/${q.id}`, { replace: true });
    },
    onError: (e: any) => { setSavedAt(null); setError(e?.response?.data?.detail ?? "Save failed."); },
  });

  const toggleInitialCategory = (id: string) =>
    setInitialCategoryIds((s) => {
      const next = new Set(s);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });

  const submit = () => {
    setError(null);
    const mixSum = form.difficultyMix.easy + form.difficultyMix.medium + form.difficultyMix.hard;
    if (mixSum !== 100) { setError(`Difficulty mix must sum to 100 (currently ${mixSum}).`); return; }
    if (new Date(form.endAt) <= new Date(form.startAt)) { setError("End must be after start."); return; }
    save.mutate();
  };

  if (!isNew && existing.isLoading) return <Loading />;

  return (
    <main className="mx-auto max-w-5xl p-6">
      <div className="mb-4 flex items-center justify-between">
        <h1 className="text-2xl font-bold text-brand-ink">{isNew ? "New quiz" : "Edit quiz"}</h1>
        <Link to="/admin/quizzes" className="text-sm text-brand hover:underline">← Back to list</Link>
      </div>

      <div className="card p-6 space-y-4">
        <Field label="Title">
          <input className="field-input" value={form.title}
            onChange={(e) => setForm({ ...form, title: e.target.value })} />
        </Field>

        <Field label="Description (candidate-visible)">
          <textarea className="field-input min-h-[3rem]" value={form.description ?? ""}
            onChange={(e) => setForm({ ...form, description: e.target.value })} />
        </Field>

        <Field label="Instructions (candidate-visible, HTML allowed)">
          <textarea className="field-input min-h-[6rem]" value={form.instructions ?? ""}
            onChange={(e) => setForm({ ...form, instructions: e.target.value })} />
        </Field>

        <Field label="Completion message (shown to candidate after submit, HTML allowed)">
          <textarea className="field-input min-h-[4rem]" value={form.completionMessage ?? ""}
            placeholder="e.g. Thank you! Your response has been recorded. Results will be released on 15 Oct."
            onChange={(e) => setForm({ ...form, completionMessage: e.target.value })} />
          <p className="mt-1 text-xs text-brand-ink/60">
            Leave blank to use the default "Thank you!" message.
          </p>
        </Field>

        <div className="grid grid-cols-2 gap-4 sm:grid-cols-4">
          <Field label="Pool size">
            <input type="number" min={1} className="field-input" value={form.poolSize}
              onChange={(e) => setForm({ ...form, poolSize: Number(e.target.value) })} />
          </Field>
          <Field label="Duration (min)">
            <input type="number" min={1} className="field-input" value={form.durationMinutes}
              onChange={(e) => setForm({ ...form, durationMinutes: Number(e.target.value) })} />
          </Field>
          <Field label="Attempts allowed">
            <input type="number" min={1} className="field-input" value={form.attemptsAllowed}
              onChange={(e) => setForm({ ...form, attemptsAllowed: Number(e.target.value) })} />
          </Field>
          <Field label="Passing score (optional)">
            <input type="number" step="0.5" className="field-input"
              value={form.passingScore ?? ""}
              onChange={(e) => setForm({ ...form, passingScore: e.target.value === "" ? null : Number(e.target.value) })} />
          </Field>
        </div>

        <div className="grid grid-cols-3 gap-4">
          <Field label="Easy %">
            <input type="number" min={0} max={100} className="field-input"
              value={form.difficultyMix.easy}
              onChange={(e) => setForm({ ...form, difficultyMix: { ...form.difficultyMix, easy: Number(e.target.value) } })} />
          </Field>
          <Field label="Medium %">
            <input type="number" min={0} max={100} className="field-input"
              value={form.difficultyMix.medium}
              onChange={(e) => setForm({ ...form, difficultyMix: { ...form.difficultyMix, medium: Number(e.target.value) } })} />
          </Field>
          <Field label="Hard %">
            <input type="number" min={0} max={100} className="field-input"
              value={form.difficultyMix.hard}
              onChange={(e) => setForm({ ...form, difficultyMix: { ...form.difficultyMix, hard: Number(e.target.value) } })} />
          </Field>
        </div>

        <div className="grid grid-cols-2 gap-4 sm:grid-cols-4">
          <Field label="Positive marks default">
            <input type="number" step="0.25" className="field-input" value={form.positiveMarksDefault}
              onChange={(e) => setForm({ ...form, positiveMarksDefault: Number(e.target.value) })} />
          </Field>
          <Field label="Negative marks default">
            <input type="number" step="0.25" className="field-input" value={form.negativeMarksDefault}
              onChange={(e) => setForm({ ...form, negativeMarksDefault: Number(e.target.value) })} />
          </Field>
          <Field label="Shortfall policy">
            <select className="field-input" value={form.shortfallPolicy}
              onChange={(e) => setForm({ ...form, shortfallPolicy: e.target.value as Shortfall })}>
              <option>Strict</option><option>Relaxed</option>
            </select>
          </Field>
          <Field label="Show results">
            <select className="field-input" value={form.showResults}
              onChange={(e) => setForm({ ...form, showResults: e.target.value as ShowResults })}>
              <option>Immediate</option><option>AfterEndDate</option><option>AdminOnly</option>
            </select>
          </Field>
        </div>

        <div className="grid grid-cols-2 gap-4 sm:grid-cols-3">
          <Field label="Starts at (local)">
            <input type="datetime-local" className="field-input" value={toLocalInput(form.startAt)}
              onChange={(e) => setForm({ ...form, startAt: fromLocalInput(e.target.value) })} />
          </Field>
          <Field label="Ends at (local)">
            <input type="datetime-local" className="field-input" value={toLocalInput(form.endAt)}
              onChange={(e) => setForm({ ...form, endAt: fromLocalInput(e.target.value) })} />
          </Field>
          <Field label="Timezone (IANA)">
            <input className="field-input" value={form.timezoneId}
              onChange={(e) => setForm({ ...form, timezoneId: e.target.value })} />
          </Field>
        </div>

        <div className="grid grid-cols-2 gap-4 sm:grid-cols-4">
          <Field label="OTP verification">
            <select className="field-input" value={form.otpMode}
              onChange={(e) => setForm({ ...form, otpMode: e.target.value as OtpMode })}>
              <option value="None">Off (no verification)</option>
              <option value="Email">Email only</option>
              <option value="Sms">SMS only</option>
              <option value="Either">Either (candidate picks)</option>
              <option value="Both">Both (email + SMS)</option>
            </select>
          </Field>
          <Field label="Require ID verification">
            <select className="field-input"
              value={String(form.idVerificationRequired)}
              onChange={(e) => setForm({ ...form, idVerificationRequired: e.target.value === "true" })}>
              <option value="true">Yes</option><option value="false">No</option>
            </select>
          </Field>
          <Field label="Randomize questions">
            <select className="field-input"
              value={String(form.randomizeQuestions)}
              onChange={(e) => setForm({ ...form, randomizeQuestions: e.target.value === "true" })}>
              <option value="true">Yes</option><option value="false">No</option>
            </select>
          </Field>
          <Field label="Randomize options">
            <select className="field-input"
              value={String(form.randomizeOptions)}
              onChange={(e) => setForm({ ...form, randomizeOptions: e.target.value === "true" })}>
              <option value="true">Yes</option><option value="false">No</option>
            </select>
          </Field>
        </div>

        <Field label="Candidate Category">
          <input className="field-input" value={candidateCategoriesText}
            placeholder="General, SC, ST, OBC"
            onChange={(e) => setCategoriesText(e.target.value)} />
          <p className="mt-1 text-xs text-brand-ink/60">
            Comma-separated values shown as the <b>Category</b> dropdown on the candidate
            registration form. Unrelated to the <b>question categories</b> that make up the
            quiz pool (those are set {isNew ? "below" : "in the Pool section further down"}).
          </p>
        </Field>

        {isNew && (
          <Field label="Question categories to include in the pool">
            {categoriesQ.data && categoriesQ.data.length > 0 ? (
              <div className="mt-1 grid grid-cols-1 gap-2 sm:grid-cols-2">
                {categoriesQ.data.map((c) => (
                  <label key={c.id} className={`flex cursor-pointer items-center gap-2 rounded-lg border px-3 py-2 text-sm transition
                    ${initialCategoryIds.has(c.id) ? "border-brand bg-brand-light" : "border-brand-ink/20 hover:bg-brand-light"}`}>
                    <input type="checkbox" className="accent-brand"
                      checked={initialCategoryIds.has(c.id)}
                      onChange={() => toggleInitialCategory(c.id)} />
                    <span className="flex-1">{c.name}</span>
                    <span className="text-xs text-brand-ink/60">{c.questionCount} q</span>
                  </label>
                ))}
              </div>
            ) : (
              <p className="text-sm text-brand-ink/60">
                No categories yet — <Link to="/admin/categories" className="text-brand hover:underline">add some first</Link>,
                or create the quiz now and attach categories afterwards.
              </p>
            )}
            <p className="mt-2 text-xs text-brand-ink/60">
              You can also attach direct questions, remove categories, and preview the pool
              after the quiz is created.
            </p>
          </Field>
        )}

        {error && <div className="text-sm text-brand" role="alert">{error}</div>}

        <div className="flex items-center justify-end gap-3 pt-2">
          {savedAt && !save.isPending && (
            <div
              role="status"
              aria-live="polite"
              className="inline-flex items-center gap-1 rounded-pill bg-emerald-100 px-3 py-1 text-sm font-medium text-emerald-800">
              <span>✓</span>
              <span>Saved at {savedAt.toLocaleTimeString()}</span>
            </div>
          )}
          <Link to="/admin/quizzes" className="btn-secondary">Cancel</Link>
          <button className="btn-primary" onClick={submit} disabled={save.isPending}>
            {save.isPending ? "Saving…" : (isNew ? "Create" : "Save")}
          </button>
        </div>
      </div>

      {!isNew && existing.data && <PoolPanel quiz={existing.data} />}
    </main>
  );
}

// ---------- Pool + preview + publish ----------
function PoolPanel({ quiz }: { quiz: Quiz }) {
  const qc = useQueryClient();
  const me = useQuery({ queryKey: ["admin-me"], queryFn: () => adminAuth.me(), retry: false });
  const canQuestions = canManageQuestions(me.data);

  const categories = useQuery({ queryKey: ["categories"], queryFn: () => adminApi.listCategories() });
  const questions = useQuery({
    queryKey: ["questions", "pool-picker"],
    queryFn: () => adminApi.listQuestions({ pageSize: 200 }),
    enabled: canQuestions,
  });
  const preview = useQuery({
    queryKey: ["pool-preview", quiz.id],
    queryFn: () => adminApi.poolPreview(quiz.id),
  });

  const invalidateAll = () => {
    qc.invalidateQueries({ queryKey: ["quiz", quiz.id] });
    qc.invalidateQueries({ queryKey: ["pool-preview", quiz.id] });
  };

  const attachCat = useMutation({ mutationFn: (id: string) => adminApi.attachCategory(quiz.id, id), onSuccess: invalidateAll });
  const detachCat = useMutation({ mutationFn: (id: string) => adminApi.detachCategory(quiz.id, id), onSuccess: invalidateAll });
  const attachQ = useMutation({ mutationFn: (id: string) => adminApi.attachQuestion(quiz.id, id), onSuccess: invalidateAll });
  const detachQ = useMutation({ mutationFn: (id: string) => adminApi.detachQuestion(quiz.id, id), onSuccess: invalidateAll });

  const publish = useMutation({
    mutationFn: () => adminApi.publishQuiz(quiz.id),
    onSuccess: invalidateAll,
    onError: (e: any) => alert(e?.response?.data?.detail ?? "Publish failed."),
  });
  const archive = useMutation({
    mutationFn: () => adminApi.archiveQuiz(quiz.id),
    onSuccess: invalidateAll,
  });

  const attachedCatIds = useMemo(() => new Set(quiz.categoryPool.map((c) => c.categoryId)), [quiz.categoryPool]);
  const attachedQIds = useMemo(() => new Set(quiz.questionPool.map((q) => q.questionId)), [quiz.questionPool]);

  return (
    <div className="mt-8 space-y-6">
      {/* Publish / archive */}
      <div className="card flex flex-wrap items-center justify-between gap-3 p-4">
        <div>
          <div className="text-sm text-brand-ink/60">Status</div>
          <div className="text-lg font-semibold">{quiz.status}</div>
        </div>
        <div className="flex gap-2">
          {quiz.status !== "Archived" && (
            <button className="btn-secondary text-brand" onClick={() => {
              if (confirm("Archive this quiz? It won't be available to candidates.")) archive.mutate();
            }}>Archive</button>
          )}
          {quiz.status === "Draft" && (
            <button className="btn-primary" onClick={() => publish.mutate()} disabled={publish.isPending}>
              {publish.isPending ? "Publishing…" : "Publish"}
            </button>
          )}
        </div>
      </div>

      {/* Pool preview */}
      <div className="card p-6">
        <h2 className="mb-3 text-lg font-semibold text-brand-ink">Pool preview</h2>
        {preview.data && (
          <>
            <div className="grid grid-cols-3 gap-4">
              <Bucket label="Easy" b={preview.data.easy} />
              <Bucket label="Medium" b={preview.data.medium} />
              <Bucket label="Hard" b={preview.data.hard} />
            </div>
            <div className="mt-3 text-sm text-brand-ink/70">
              Total available: <b>{preview.data.totalAvailable}</b> · Requested: <b>{preview.data.requestedPoolSize}</b>
            </div>
            {preview.data.warnings.length > 0 && (
              <ul className="mt-3 space-y-1 text-sm text-brand">
                {preview.data.warnings.map((w, i) => <li key={i}>• {w}</li>)}
              </ul>
            )}
          </>
        )}
      </div>

      {/* Attached categories */}
      <div className="card p-6">
        <h2 className="mb-3 text-lg font-semibold text-brand-ink">Category pool</h2>
        <div className="mb-3 flex flex-wrap gap-2">
          {quiz.categoryPool.map((c) => (
            <span key={c.categoryId} className="inline-flex items-center gap-2 rounded-pill bg-brand-light px-3 py-1 text-sm">
              {c.name} <span className="text-brand-ink/60">({c.questionCount})</span>
              <button className="text-brand hover:underline" onClick={() => detachCat.mutate(c.categoryId)}>×</button>
            </span>
          ))}
          {quiz.categoryPool.length === 0 && <span className="text-sm text-brand-ink/60">No categories attached.</span>}
        </div>
        <select className="field-input max-w-sm"
          value=""
          onChange={(e) => { if (e.target.value) attachCat.mutate(e.target.value); }}>
          <option value="">+ Attach a category…</option>
          {categories.data?.filter((c) => !attachedCatIds.has(c.id))
            .map((c) => <option key={c.id} value={c.id}>{c.name} ({c.questionCount})</option>)}
        </select>
      </div>

      {/* Attached direct questions — Admin/SuperAdmin only. Instructors can't see the question bank. */}
      {canQuestions && (
      <div className="card p-6">
        <h2 className="mb-3 text-lg font-semibold text-brand-ink">Direct questions</h2>
        <div className="mb-3 space-y-2">
          {quiz.questionPool.map((q) => (
            <div key={q.questionId} className="flex items-center justify-between rounded-lg bg-brand-light px-3 py-2 text-sm">
              <span className="truncate">
                <span className="mr-2 text-xs text-brand-ink/60">[{q.categoryName} · {q.difficulty}]</span>
                {q.text}
              </span>
              <button className="ml-3 text-brand hover:underline" onClick={() => detachQ.mutate(q.questionId)}>Remove</button>
            </div>
          ))}
          {quiz.questionPool.length === 0 && <span className="text-sm text-brand-ink/60">No direct questions.</span>}
        </div>
        <select className="field-input max-w-2xl"
          value=""
          onChange={(e) => { if (e.target.value) attachQ.mutate(e.target.value); }}>
          <option value="">+ Attach a question…</option>
          {questions.data?.items.filter((q) => !attachedQIds.has(q.id))
            .map((q) => <option key={q.id} value={q.id}>[{q.categoryName} · {q.difficulty}] {q.text.slice(0, 100)}</option>)}
        </select>
      </div>
      )}
    </div>
  );
}

function Bucket({ label, b }: { label: string; b: { required: number; available: number; shortfall: number } }) {
  const short = b.shortfall > 0;
  return (
    <div className={`rounded-xl p-3 ${short ? "bg-brand-light" : "bg-emerald-50"}`}>
      <div className="text-xs uppercase tracking-wide text-brand-ink/60">{label}</div>
      <div className="mt-1 text-2xl font-bold text-brand-ink">
        {b.available} <span className="text-brand-ink/50">/ {b.required}</span>
      </div>
      {short && <div className="mt-1 text-xs text-brand">short by {b.shortfall}</div>}
    </div>
  );
}

// ---------- Helpers ----------
function blankForm(): QuizInput {
  const start = new Date();
  const end = new Date(start.getTime() + 30 * 24 * 60 * 60 * 1000);
  return {
    title: "",
    description: "",
    instructions: "",
    completionMessage: "",
    poolSize: 10,
    difficultyMix: { easy: 33, medium: 34, hard: 33 },
    positiveMarksDefault: 1,
    negativeMarksDefault: 0,
    durationMinutes: 30,
    startAt: start.toISOString(),
    endAt: end.toISOString(),
    timezoneId: "Asia/Kolkata",
    randomizeQuestions: true,
    randomizeOptions: true,
    passingScore: null,
    attemptsAllowed: 1,
    showResults: "AdminOnly",
    idVerificationRequired: true,
    proctorSettings: defaultProctorSettings(),
    shortfallPolicy: "Strict",
    otpMode: "Either",
    candidateCategories: ["General", "SC", "ST", "OBC"],
  };
}
function quizToInput(q: Quiz): QuizInput {
  return {
    title: q.title, description: q.description, instructions: q.instructions,
    completionMessage: q.completionMessage ?? "",
    poolSize: q.poolSize, difficultyMix: q.difficultyMix,
    positiveMarksDefault: q.positiveMarksDefault, negativeMarksDefault: q.negativeMarksDefault,
    durationMinutes: q.durationMinutes,
    startAt: q.startAt, endAt: q.endAt, timezoneId: q.timezoneId,
    randomizeQuestions: q.randomizeQuestions, randomizeOptions: q.randomizeOptions,
    passingScore: q.passingScore, attemptsAllowed: q.attemptsAllowed,
    showResults: q.showResults, idVerificationRequired: q.idVerificationRequired,
    proctorSettings: q.proctorSettings,
    shortfallPolicy: q.shortfallPolicy, otpMode: q.otpMode,
    candidateCategories: q.candidateCategories,
  };
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div>
      <label className="field-label">{label}</label>
      <div className="mt-1">{children}</div>
    </div>
  );
}
function Loading() { return <main className="grid min-h-[40vh] place-items-center text-brand-ink/60">Loading…</main>; }
